import * as AddressFormatter from '@shopify/address/build/ts/index';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Setting } from '../../types/constant/PagesPropsType';
import changeDeliveryAddressClass from '../../assets/scss/SubscriptionDetail/ChangeDeliveryAddress.module.scss';
import { ChangeDeliveryAddressViewType } from '../../types/view/ChangeDeliveryAddressViewType';
import { postMessageGtag } from '../../utils/trackClick';
import {
  AddressCandidateType,
  SearchZipcodeResultType,
  useSearchZipcode,
  validateAndFormatZip,
} from '../../hooks/use-search-zipcode';
import { truncateFullWidth } from '../../utils/formats';
import { Scrollbars } from 'react-custom-scrollbars-2';

type SelectedResultType = AddressCandidateType & {
  zipcode: string;
};

export const ChangeDeliveryAddress: FC<{
  loading: boolean;
  changeDeliveryAddress: ChangeDeliveryAddressViewType | null;
  setting: Setting;
  clickUpdateBtnFunc: (valid: boolean) => void;
  changeValueFunc: (name: string, value: string) => void;
}> = ({ loading, changeDeliveryAddress, setting, clickUpdateBtnFunc, changeValueFunc }) => {
  const form = useRef(null);
  const clickUpdateBtnHandler = useCallback(() => {
    clickUpdateBtnFunc((form as any).current.checkValidity());
  }, [clickUpdateBtnFunc]);

  const [provinceOptionsHtml, setProvinceOptionsHtml] = useState<JSX.Element[] | JSX.Element>(
    <></>
  );
  useEffect(() => {
    (async () => {
      const addressFormatter = new AddressFormatter.default('ja');
      const resCountry = await addressFormatter.getCountry('JP'); //日本固定

      if (resCountry) {
        const provinceOptionsHtml = resCountry.zones.map((zone, idx) => (
          <option
            value={zone.code}
            key={`provinceOption-${idx}`}
          >
            {zone.name}
          </option>
        ));
        setProvinceOptionsHtml(provinceOptionsHtml);
      }
    })();
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const toggleClass = () => {
    if (!isOpen) {
      // アコーディオンが開く分の高さ調整
      const height = document.querySelector('body')!.scrollHeight;
      setting.resize(height + 700);
    }
    setIsOpen(!isOpen);
  };

  const changeValueHandler = useCallback(
    (e) => {
      changeValueFunc(e.target.name, e.target.value);
      e.target.reportValidity();
    },
    [changeValueFunc]
  );

  // ↓=========== 住所自動入力 ===========↓
  const { searchZipcode } = useSearchZipcode();
  const [isSearchError, setIsSearchError] = useState(false);
  const [selectedResult, setSelectedResult] = useState<SelectedResultType | null>(null);
  const [searchResults, setSearchResults] = useState<SearchZipcodeResultType | null>(null);
  const [isSelectedResultApplied, setIsSelectedResultApplied] = useState(false);

  const searchByZipcode = async () => {
    if (!changeDeliveryAddress?.deliveryZip) return;

    const results = await searchZipcode(changeDeliveryAddress.deliveryZip);

    setIsSearchError(false);
    setSelectedResult(null);
    setSearchResults(null);
    setIsSelectedResultApplied(false);

    if (results.candidates.length === 0) {
      // 一致候補なし
      setIsSearchError(true);
    } else {
      // 自動入力 郵便番号は000-0000の形
      const zipcode = results.zipcode.slice(0, 3) + '-' + results.zipcode.slice(3);
      if (results.candidates.length === 1) {
        const result = results.candidates[0];
        setSelectedResult({
          zipcode,
          provinceCode: result.provinceCode,
          city: result.city,
          address1: result.address1,
          prefName: result.prefName,
        });
      } else {
        setSearchResults({ zipcode, candidates: results.candidates });
      }
    }
  };

  useEffect(() => {
    if (!selectedResult || isSelectedResultApplied) return;

    // 住所自動入力されたら
    changeValueFunc('deliveryZip', selectedResult.zipcode);
    changeValueFunc('deliveryProvinceCode', selectedResult.provinceCode);
    changeValueFunc('deliveryCity', selectedResult.city);
    changeValueFunc('deliveryAddress1', selectedResult.address1);

    setIsSelectedResultApplied(true);
  }, [changeValueFunc, isSelectedResultApplied, selectedResult]);

  useEffect(() => {
    setIsSearchError(false);
  }, [changeDeliveryAddress?.deliveryZip]);

  // 検索ボタンの状態管理
  const [isSearchDisabled, setIsSearchDisabled] = useState(true);

  useEffect(() => {
    if (!changeDeliveryAddress?.deliveryZip) return;

    if (!validateAndFormatZip(changeDeliveryAddress.deliveryZip)) {
      setIsSearchDisabled(true);
    } else {
      setIsSearchDisabled(false);
    }
  }, [changeDeliveryAddress?.deliveryZip]);

  // 候補枠外検知
  const zipCandidatesRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!searchResults || searchResults.candidates.length === 0) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (zipCandidatesRef.current && !zipCandidatesRef.current.contains(event.target as Node)) {
        setSearchResults(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchResults]);

  // ↑=========== 住所自動入力 ===========↑

  // スクロールバー
  const ulRef = useRef<HTMLUListElement>(null);
  const [scrollHeight, setScrollHeight] = useState<number | 'auto'>('auto');
  const MAX_HEIGHT = 210;

  useEffect(() => {
    if (ulRef.current) {
      const ulHeight = ulRef.current.scrollHeight;

      setScrollHeight(ulHeight < MAX_HEIGHT ? ulHeight : MAX_HEIGHT);
    }
  }, [searchResults]);

  if (!changeDeliveryAddress) return <></>;

  const content = (
    <>
      <div className={changeDeliveryAddressClass.changeDeliveryAddress}>
        <button
          className={changeDeliveryAddressClass.title}
          onClick={() => {
            toggleClass();
            postMessageGtag('click_open_address', 'detailSettings');
          }}
        >
          配送先住所を変更する
          <span
            className={`${
              isOpen ? changeDeliveryAddressClass.angleUp : changeDeliveryAddressClass.angleDown
            }`}
          ></span>
        </button>
        <div
          className={`${
            isOpen ? changeDeliveryAddressClass.open : changeDeliveryAddressClass.closed
          }`}
        >
          {isOpen && (
            <form
              ref={form}
              className={changeDeliveryAddressClass.addressForm}
            >
              <div className={changeDeliveryAddressClass.inputContainer}>
                <label>姓</label>
                <input
                  type="text"
                  name="deliveryLastName"
                  value={changeDeliveryAddress.deliveryLastName}
                  required
                  onChange={changeValueHandler}
                  placeholder="山田"
                />
              </div>
              <div className={changeDeliveryAddressClass.inputContainer}>
                <label>名</label>
                <input
                  type="text"
                  name="deliveryFirstName"
                  value={changeDeliveryAddress.deliveryFirstName}
                  required
                  onChange={changeValueHandler}
                  placeholder="太郎"
                />
              </div>
              <div
                className={`${changeDeliveryAddressClass.inputContainer} ${changeDeliveryAddressClass.w100}`}
              >
                <label>郵便番号</label>
                <div className={changeDeliveryAddressClass.zipContainer}>
                  <input
                    type="text"
                    name="deliveryZip"
                    value={changeDeliveryAddress.deliveryZip}
                    required
                    onChange={changeValueHandler}
                    placeholder="123-4567"
                    className={isSearchError ? changeDeliveryAddressClass.inputError : ''}
                  />
                  <button
                    type="button"
                    onClick={searchByZipcode}
                    disabled={isSearchDisabled}
                  >
                    検索
                  </button>
                  {searchResults && searchResults.candidates.length > 0 && (
                    <div
                      className={changeDeliveryAddressClass.zipCandidatesContainer}
                      ref={zipCandidatesRef}
                    >
                      <div className={changeDeliveryAddressClass.zipCandidatesHeader}>候補</div>
                      <Scrollbars
                        style={{ height: scrollHeight, maxHeight: MAX_HEIGHT }}
                        className={changeDeliveryAddressClass.zipCandidatesScroll}
                        renderThumbVertical={({ style, ...props }) => (
                          <div
                            {...props}
                            style={{
                              ...style,
                              backgroundColor: '#d9d9d9',
                              borderRadius: '7px',
                              right: '10px',
                              width: '5px',
                            }}
                          />
                        )}
                        autoHide={false}
                      >
                        <ul
                          className={changeDeliveryAddressClass.zipCandidatesList}
                          ref={ulRef}
                        >
                          {searchResults.candidates.map((candidate, idx) => (
                            <li
                              key={`candidate-${idx}`}
                              onClick={() => {
                                // 選択時にselectedResultをセット
                                setSelectedResult({
                                  zipcode: searchResults.zipcode,
                                  provinceCode: candidate.provinceCode,
                                  city: candidate.city,
                                  address1: candidate.address1,
                                  prefName: candidate.prefName,
                                });
                                // 候補リストを閉じるためにクリア
                                setSearchResults(null);
                              }}
                            >
                              {truncateFullWidth(
                                candidate.prefName +
                                  ' ' +
                                  candidate.city +
                                  ' ' +
                                  candidate.address1,
                                17
                              )}
                            </li>
                          ))}
                        </ul>
                      </Scrollbars>
                    </div>
                  )}
                </div>
                {isSearchError && (
                  <div className={changeDeliveryAddressClass.searchErrorText}>
                    該当する郵便番号が見つかりませんでした。
                  </div>
                )}
                <div className={changeDeliveryAddressClass.searchInfoText}>
                  都道府県・市区町村が自動入力されます
                </div>
              </div>
              <div
                className={`${changeDeliveryAddressClass.inputContainer} ${changeDeliveryAddressClass.selectAngle}`}
              >
                <label>都道府県</label>
                <select
                  name="deliveryProvinceCode"
                  value={changeDeliveryAddress.deliveryProvinceCode}
                  onChange={changeValueHandler}
                >
                  {provinceOptionsHtml}
                </select>
              </div>
              <div className={changeDeliveryAddressClass.inputContainer}>
                <label>市区町村</label>
                <input
                  type="text"
                  name="deliveryCity"
                  value={changeDeliveryAddress.deliveryCity}
                  required
                  onChange={changeValueHandler}
                />
              </div>
              <div
                className={`${changeDeliveryAddressClass.inputContainer} ${changeDeliveryAddressClass.w100}`}
              >
                <label>住所</label>
                <input
                  type="text"
                  name="deliveryAddress1"
                  value={changeDeliveryAddress.deliveryAddress1}
                  required
                  onChange={changeValueHandler}
                />
              </div>
              <div
                className={`${changeDeliveryAddressClass.inputContainer} ${changeDeliveryAddressClass.w100}`}
              >
                <label>建物名、部屋番号</label>
                <input
                  type="text"
                  name="deliveryAddress2"
                  value={changeDeliveryAddress.deliveryAddress2}
                  onChange={changeValueHandler}
                />
              </div>
              <div
                className={`${changeDeliveryAddressClass.inputContainer} ${changeDeliveryAddressClass.w100}`}
              >
                <label>会社名</label>
                <input
                  type="text"
                  name="deliveryCompany"
                  value={changeDeliveryAddress.deliveryCompany}
                  onChange={changeValueHandler}
                />
              </div>
              <div
                className={`${changeDeliveryAddressClass.inputContainer} ${changeDeliveryAddressClass.w100}`}
              >
                <label>電話番号</label>
                <input
                  type="text"
                  name="deliveryPhone"
                  value={changeDeliveryAddress.deliveryPhone}
                  onChange={changeValueHandler}
                  placeholder="090-1234-5678"
                />
              </div>
            </form>
          )}
          <div className={changeDeliveryAddressClass.btnContainer}>
            <button
              onClick={() => {
                clickUpdateBtnHandler();
                postMessageGtag('click_address', 'detailSettings');
              }}
              disabled={loading}
            >
              変更する
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return content;
};
